import type { WidgetBlock, WidgetConfig } from "@workspace/contracts/embed"

import { partClass, type Look } from "../appearance"
import { X } from "../icons"
import type { SurfaceProps } from "../surface"

/**
 * The exit popup: a scrim over the page, a card in the middle, the blocks
 * in their order - the preview's styles number for number. Each block
 * carries the customer's `cssClass` beside the runtime's own; its `css`
 * declarations go into the mount's stylesheet under `[data-uq-block=i]`
 * (`mount.ts`), so they apply to that block alone.
 */

type PopupConfig = Extract<WidgetConfig, { type: "exit-popup" }>

/** A media block renders only from a URL the browser can load; a bare storage key stays blank. */
const isUrl = (src: string) => /^https?:\/\//i.test(src)

function Block({
  config,
  block,
  index,
  look,
  onCta,
}: {
  config: PopupConfig
  block: WidgetBlock
  index: number
  look: Look
  onCta: (href: string) => void
}) {
  const { ink, accent, accentFill, accentText, rad } = look
  const own = ["uq-block", `uq-block-${block.kind}`, block.cssClass.trim()]
    .filter(Boolean)
    .join(" ")
  const align = "align" in block ? block.align : "center"
  const hook = { class: own, "data-uq-block": index }

  switch (block.kind) {
    case "headline":
      return (
        <div
          {...hook}
          style={{
            fontFamily: "var(--font-display, inherit)",
            fontSize: block.size === "lg" ? 21 : block.size === "sm" ? 15 : 18,
            fontWeight: 600,
            letterSpacing: "-0.02em",
            textAlign: align,
            color: block.color || `rgb(${ink})`,
          }}
        >
          {block.text}
        </div>
      )
    case "text":
      return (
        <div
          {...hook}
          style={{
            fontSize: 12.5,
            lineHeight: 1.5,
            textAlign: align,
            color: block.color || `rgba(${ink},.65)`,
            whiteSpace: "pre-wrap",
          }}
        >
          {block.text}
        </div>
      )
    case "media": {
      if (!block.src || !isUrl(block.src)) return null
      return block.mediaType === "video" ? (
        <video
          {...hook}
          src={block.src}
          muted
          loop
          autoPlay
          playsInline
          style={{
            width: "100%",
            maxHeight: 200,
            objectFit: "cover",
            borderRadius: rad,
            background: "#000",
          }}
        />
      ) : (
        <img
          {...hook}
          src={block.src}
          alt={block.alt || ""}
          style={{
            width: "100%",
            maxHeight: 200,
            objectFit: "cover",
            borderRadius: rad,
          }}
        />
      )
    }
    case "quote":
      return (
        <div {...hook} style={{ textAlign: "center" }}>
          <div
            style={{ fontSize: 13, fontStyle: "italic", color: `rgb(${ink})` }}
          >
            “{block.text}”
          </div>
          <div
            style={{ fontSize: 11, color: `rgba(${ink},.55)`, marginTop: 4 }}
          >
            {block.author}
          </div>
        </div>
      )
    case "cta":
      return (
        <a
          {...hook}
          class={`${partClass(config, "cta")} ${own}`}
          href={block.href || "#"}
          target={block.newTab ? "_blank" : undefined}
          rel={block.newTab ? "noopener noreferrer" : undefined}
          onClick={() => onCta(block.href)}
          style={{
            display: "block",
            marginTop: 4,
            padding: "10px 14px",
            borderRadius: rad,
            background: block.style === "outline" ? "transparent" : accentFill,
            border:
              block.style === "outline" ? `1px solid ${accent}` : undefined,
            color: block.style === "outline" ? accent : accentText,
            fontSize: 13,
            fontWeight: 500,
            textAlign: "center",
          }}
        >
          {block.label}
        </a>
      )
  }
}

export function ExitPopupSurface({
  payload,
  look,
  reporter,
  open,
  setOpen,
  pinned,
}: SurfaceProps) {
  const config = payload.config as PopupConfig
  const { ink, surfaceBg, rad } = look
  if (!open) return null

  const close = () => {
    if (!pinned) setOpen(false)
  }
  const onCta = (href: string) =>
    reporter.report("cta.clicked", { widgetId: payload.id, payload: { href } })

  return (
    <div
      class={partClass(config, "scrim", "uq-fixed uq-fade")}
      onClick={close}
      style={{
        inset: 0,
        background: "rgba(8,10,16,.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        class={partClass(config, "card", "uq-rise")}
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 340,
          // A tall lineup of blocks scrolls inside the card on a short screen.
          maxHeight: "calc(100vh - 48px)",
          overflowY: "auto",
          background: surfaceBg,
          borderRadius: rad,
          padding: "22px 20px",
          boxShadow: "0 24px 60px rgba(0,0,0,.3)",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          textAlign: "center",
          color: `rgb(${ink})`,
        }}
      >
        {!pinned && (
          <button
            type="button"
            class={partClass(config, "close")}
            onClick={close}
            aria-label="Close"
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              width: 24,
              height: 24,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: `rgba(${ink},.55)`,
            }}
          >
            <X size={14} />
          </button>
        )}
        {config.blocks.map((block, index) => (
          <Block
            key={index}
            config={config}
            block={block}
            index={index}
            look={look}
            onCta={onCta}
          />
        ))}
      </div>
    </div>
  )
}
